/**
 * Credit Entry Form Component
 * 
 * Form for creating new credits (claims, returns, overpayments, etc.)
 * linked to an origin shipment.
 */

import React, { useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Modal,
  Space,
  Typography,
  Divider,
  message,
  AutoComplete,
  Spin,
  Card,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  DollarOutlined,
  SearchOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { creditsApi } from '../../services/api';
import api from '../../services/api';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Credit type options
const creditTypes = [
  { value: 'CLAIM', label: 'Claim', description: 'Quality or damage claim' },
  { value: 'RETURN', label: 'Return', description: 'Product return' },
  { value: 'OVERPAYMENT', label: 'Overpayment', description: 'Payment exceeded amount due' },
  { value: 'PRICING_ADJUSTMENT', label: 'Pricing Adjustment', description: 'Price correction' },
  { value: 'OTHER', label: 'Other', description: 'Other credit type' },
];

// Party type options
const partyTypes = [
  { value: 'BUYER', label: 'Buyer' },
  { value: 'SUPPLIER', label: 'Supplier' },
];

interface ShipmentSearchResult {
  id: string;
  reference: string;
  contract?: {
    reference: string;
    buyer?: { id: string; name: string; shortName: string };
    supplier?: { id: string; name: string; shortName: string };
  };
}

interface CreditEntryFormProps {
  /** Open modal with pre-selected shipment */
  shipmentId?: string;
  /** Called when credit is successfully created */
  onSuccess?: (credit: any) => void;
  /** Render as a button that opens modal, or inline form */
  mode?: 'button' | 'inline';
}

const CreditEntryForm: React.FC<CreditEntryFormProps> = ({
  shipmentId: initialShipmentId,
  onSuccess,
  mode = 'button',
}) => {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [shipmentSearch, setShipmentSearch] = useState('');
  const [selectedShipment, setSelectedShipment] = useState<ShipmentSearchResult | null>(null);
  const queryClient = useQueryClient();

  // Search shipments
  const { data: shipmentResults, isLoading: searchingShipments } = useQuery({
    queryKey: ['shipment-search', shipmentSearch],
    queryFn: async () => {
      if (!shipmentSearch || shipmentSearch.length < 2) return [];
      // Strip shipment suffix (.01, .02, etc.) from contract refs for search
      const searchTerm = shipmentSearch.replace(/(\.\d{2})$/, '');
      // Use /contracts endpoint with search param and include=shipments
      const response = await api.get('/contracts', {
        params: { search: searchTerm, limit: 10, include: 'shipments' },
      });
      // Extract shipments from contracts
      const shipments: ShipmentSearchResult[] = [];
      for (const contract of (response as any).contracts || []) {
        for (const shipment of contract.shipments || []) {
          shipments.push({
            id: shipment.id,
            reference: shipment.reference || `${contract.reference}-${shipment.id.slice(-4)}`,
            contract: {
              reference: contract.reference,
              buyer: contract.buyer,
              supplier: contract.supplier,
            },
          });
        }
      }
      return shipments;
    },
    enabled: shipmentSearch.length >= 2,
  });

  // Create credit mutation
  const createCreditMutation = useMutation({
    mutationFn: (data: any) => creditsApi.create(data),
    onSuccess: (response: any) => {
      message.success('Credit created successfully');
      queryClient.invalidateQueries({ queryKey: ['credits'] });
      queryClient.invalidateQueries({ queryKey: ['credits-summary'] });
      form.resetFields();
      setSelectedShipment(null);
      setModalVisible(false);
      onSuccess?.(response);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create credit');
    },
  });

  // Handle shipment selection
  const handleShipmentSelect = (shipment: ShipmentSearchResult) => {
    setSelectedShipment(shipment);
    setShipmentSearch(shipment.reference);
    
    // Auto-fill party based on party type
    const partyType = form.getFieldValue('partyType');
    if (partyType && shipment.contract) {
      const party = partyType === 'BUYER' ? shipment.contract.buyer : shipment.contract.supplier;
      if (party) {
        form.setFieldsValue({
          partyName: party.shortName || party.name,
          partyId: party.id,
        });
      }
    }
  };

  // Handle party type change
  const handlePartyTypeChange = (partyType: string) => {
    if (selectedShipment?.contract) {
      const party = partyType === 'BUYER' 
        ? selectedShipment.contract.buyer 
        : selectedShipment.contract.supplier;
      if (party) {
        form.setFieldsValue({
          partyName: party.shortName || party.name,
          partyId: party.id,
        });
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (values: any) => {
    if (!selectedShipment) {
      message.error('Please select an origin shipment');
      return;
    }

    const creditData = {
      originShipmentId: selectedShipment.id,
      partyType: values.partyType,
      partyId: values.partyId,
      partyName: values.partyName,
      creditType: values.creditType,
      originalAmount: values.amount,
      currencyCode: values.currencyCode || 'USD',
      description: values.description,
      referenceNumber: values.referenceNumber,
    };

    createCreditMutation.mutate(creditData);
  };

  const formContent = (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        partyType: 'BUYER',
        currencyCode: 'USD',
      }}
    >
      {/* Origin Shipment Search */}
      <Form.Item
        label="Origin Shipment"
        required
        help="Search by shipment reference, contract reference, or party name"
      >
        <AutoComplete
          value={shipmentSearch}
          onChange={setShipmentSearch}
          options={(shipmentResults || []).map((s) => ({
            value: s.reference,
            label: (
              <div>
                <Text strong>{s.reference}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {s.contract?.reference} | {s.contract?.buyer?.shortName || s.contract?.buyer?.name} / {s.contract?.supplier?.shortName || s.contract?.supplier?.name}
                </Text>
              </div>
            ),
            shipment: s,
          }))}
          onSelect={(_, option: any) => handleShipmentSelect(option.shipment)}
          placeholder="Search shipment..."
          style={{ width: '100%' }}
          notFoundContent={
            searchingShipments ? <Spin size="small" /> : 
            shipmentSearch.length < 2 ? 'Type at least 2 characters' : 'No shipments found'
          }
        >
          <Input prefix={<SearchOutlined />} />
        </AutoComplete>
      </Form.Item>

      {selectedShipment && (
        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
              <Text strong>Selected: {selectedShipment.reference}</Text>
            </div>
            <Text type="secondary">
              Contract: {selectedShipment.contract?.reference} | 
              Buyer: {selectedShipment.contract?.buyer?.shortName || selectedShipment.contract?.buyer?.name || 'N/A'} | 
              Supplier: {selectedShipment.contract?.supplier?.shortName || selectedShipment.contract?.supplier?.name || 'N/A'}
            </Text>
          </Space>
        </Card>
      )}

      <Divider />

      {/* Party Selection */}
      <Form.Item
        label="Party Type"
        name="partyType"
        rules={[{ required: true, message: 'Please select party type' }]}
      >
        <Select onChange={handlePartyTypeChange}>
          {partyTypes.map((pt) => (
            <Option key={pt.value} value={pt.value}>{pt.label}</Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Party Name"
        name="partyName"
        rules={[{ required: true, message: 'Please enter party name' }]}
      >
        <Input placeholder="Party name" />
      </Form.Item>

      <Form.Item name="partyId" hidden>
        <Input />
      </Form.Item>

      <Divider />

      {/* Credit Details */}
      <Form.Item
        label="Credit Type"
        name="creditType"
        rules={[{ required: true, message: 'Please select credit type' }]}
      >
        <Select placeholder="Select credit type">
          {creditTypes.map((ct) => (
            <Option key={ct.value} value={ct.value}>
              <div>
                <Text>{ct.label}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>{ct.description}</Text>
              </div>
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Amount"
        name="amount"
        rules={[
          { required: true, message: 'Please enter amount' },
          { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
        ]}
      >
        <InputNumber
          prefix={<DollarOutlined />}
          style={{ width: '100%' }}
          precision={2}
          min={0.01}
          placeholder="0.00"
        />
      </Form.Item>

      <Form.Item
        label="Currency"
        name="currencyCode"
      >
        <Select style={{ width: 120 }}>
          <Option value="USD">USD</Option>
          <Option value="EUR">EUR</Option>
          <Option value="GBP">GBP</Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Reference Number"
        name="referenceNumber"
        help="Optional: claim number, RMA number, etc."
      >
        <Input placeholder="Optional reference" />
      </Form.Item>

      <Form.Item
        label="Description"
        name="description"
        help="Describe the reason for this credit"
      >
        <TextArea rows={3} placeholder="Description of credit..." />
      </Form.Item>

      {mode === 'inline' && (
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={createCreditMutation.isPending}
            icon={<PlusOutlined />}
          >
            Create Credit
          </Button>
        </Form.Item>
      )}
    </Form>
  );

  if (mode === 'inline') {
    return formContent;
  }

  return (
    <>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setModalVisible(true)}
      >
        New Credit
      </Button>

      <Modal
        title="Create Credit"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setSelectedShipment(null);
          setShipmentSearch('');
        }}
        onOk={() => form.submit()}
        confirmLoading={createCreditMutation.isPending}
        width={600}
        okText="Create Credit"
      >
        {formContent}
      </Modal>
    </>
  );
};

export default CreditEntryForm;
